import { sagaIntl } from '@i18n';
import { sortBy } from 'lodash';
import { CaseReducer, createReducer } from '@reduxjs/toolkit';

import * as actions from '../actions';
import { defaultState } from '../store';

import { produce, produceWithPatches } from 'immer';
import { BackgroundBlurSlice } from '../store.models';
import {
  convertFrameIndexToTimeMs,
  findFrameIndex,
  frameAlignStartTimeMs,
  frameAlignStopTimeMs,
} from '@utils';
import { BACKGROUND_BLUR_ACTION_TYPE } from '@redact/components/Timeline/Timelines/BackgroundBlur';
import { isImage } from '@helpers/tdoHelper';
import { markTdoChanged } from './tdoChanged';

type Re<P = unknown> = CaseReducer<
  typeof defaultState,
  { payload: P; type: string }
>;

const onBackgroundBlurSlice: Re<actions.BackgroundBlurSliceRequest> = (
  state,
  { payload }
) => {
  const intl = sagaIntl();
  const { slice, reset, actionType } = payload;
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      if (reset) {
        draft.backgroundBlurEdits = subtractEditSlices(
          draft.backgroundBlurEdits,
          reset,
          draft.tdo?.details.veritoneFile?.videoFrameRate
        );
      }

      draft.backgroundBlurEdits = insertEditSlice(
        draft.backgroundBlurEdits,
        slice,
        draft.tdo?.details.veritoneFile?.videoFrameRate
      );
    }
  );

  return produce(nextState, (draft) => {
    let description: string | undefined;
    if (actionType === BACKGROUND_BLUR_ACTION_TYPE.ADD) {
      description = intl.formatMessage({
        id: 'addBackgroundBlurSlice',
        defaultMessage: 'add background blur slice',
      });
    } else if (actionType === BACKGROUND_BLUR_ACTION_TYPE.UPDATE) {
      description = intl.formatMessage({
        id: 'updateBackgroundBlur',
        defaultMessage: 'update background blur',
      });
    }
    if (description && patches.length && inversePatches.length) {
      draft.history.past.push({
        patches,
        inversePatches,
        metadata: { description },
      });
      draft.history.future = [];
    }
  });
};

const onResetSlice: Re<actions.ResetBackgroundBlurSliceRequest> = (
  state,
  { payload }
) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);

      draft.backgroundBlurEdits = subtractEditSlices(
        draft.backgroundBlurEdits,
        payload,
        draft.tdo?.details.veritoneFile?.videoFrameRate
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'removeBackgroundBlurSlice',
          defaultMessage: 'remove background blur slice',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onUpdateBackgroundBlurLevels: Re<
  actions.UpdateBackgroundBlurLevelsRequest
> = (state, { payload }) => {
  const intl = sagaIntl();
  const { blurLevel } = payload;
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);

      // If no background blur exists yet, create a default slice — but only
      // for images, For videos with no prior edits we preserve the previous
      // "do nothing when empty" semantics.
      if (draft.backgroundBlurEdits.length === 0) {
        const tdo = draft.tdo;
        // IMAGE-TODO centralize where we determine if media is image or video, and reuse here and in editor header where we currently also determine this
        const mediaIsImage =
          !!tdo &&
          isImage({
            primaryAsset: tdo.primaryAsset
              ? { contentType: tdo.primaryAsset.contentType }
              : undefined,
            name: tdo.name ?? undefined,
          });
        if (mediaIsImage) {
          draft.backgroundBlurEdits = [
            [0, 1000, { blurLevel }], // For images, we use a  1 second duration since they don't have a real duration
          ];
        }
      } else {
        // Update existing slices with new blur level
        let slices: BackgroundBlurSlice[] = [];

        draft.backgroundBlurEdits.forEach(
          (slice) =>
            (slices = insertEditSlice(
              slices,
              [slice[0], slice[1], { ...slice[2], blurLevel }],
              draft.tdo?.details.veritoneFile?.videoFrameRate
            ))
        );

        draft.backgroundBlurEdits = slices;
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'updateBackgroundBlurLevels',
          defaultMessage: 'update background blur levels',
        }),
      },
    });
    draft.history.future = [];
  });
};

/**
const onRemoveAllSlices: Re<
  actions.RemoveAllBackgroundBlurRequest
> = (state) => ({
  ...state,
  ...tdoChangedFields(state),
  backgroundBlurEdits: []
  },
});
*/

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(actions.BACKGROUND_BLUR_SLICE, onBackgroundBlurSlice)
    .addCase(actions.RESET_BACKGROUND_BLUR_SLICE, onResetSlice)
    // .addCase(actions.REMOVE_ALL_BACKGROUND_BLUR, onRemoveAllSlices) // not implemented
    .addCase(
      actions.UPDATE_BACKGROUND_BLUR_LEVELS,
      onUpdateBackgroundBlurLevels
    );
});

/**
 * Takes a list of existing slices an insert a new splice
 */
export const insertEditSlice = (
  initSlices: Array<BackgroundBlurSlice>,
  insertSlice: BackgroundBlurSlice,
  fps?: number
): Array<BackgroundBlurSlice> => {
  const sortedSlices = sortBy(initSlices, '0'); // this shouldn't be necessary

  const outSlices: BackgroundBlurSlice[] = [];
  let insertIndex = 0;

  // frame alignment
  let newSlice = insertSlice;
  if (fps) {
    newSlice = [
      Math.ceil(frameAlignStartTimeMs(insertSlice[0], fps)),
      Math.floor(frameAlignStopTimeMs(insertSlice[1], fps)),
      insertSlice[2],
    ];
  }

  let existingSlices = sortedSlices;
  if (fps) {
    existingSlices = sortedSlices.map((s) => [
      Math.ceil(frameAlignStartTimeMs(s[0], fps)),
      Math.floor(frameAlignStopTimeMs(s[1], fps)),
      s[2],
    ]);
  }

  existingSlices.forEach((slice) => {
    // before insert
    if (slice[1] < newSlice[0]) {
      outSlices.push(slice);
      insertIndex += 1;
      return;
    }

    // overlapping - cut end of existing slice
    if (slice[0] < newSlice[0] && slice[1] >= newSlice[0]) {
      if (slice[2]?.blurLevel === newSlice[2]?.blurLevel) {
        newSlice[0] = slice[0]; // same blur level then will merge newSlice with left slice
      } else {
        // cut end of old slice
        if (fps) {
          const newSliceStartIndex = findFrameIndex(newSlice[0], fps);
          const leftSliceEnd = Math.floor(
            frameAlignStopTimeMs(
              convertFrameIndexToTimeMs(newSliceStartIndex - 1, fps) + 1,
              fps
            )
          );
          outSlices.push([slice[0], leftSliceEnd, slice[2]]);
        } else {
          outSlices.push([slice[0], newSlice[0], slice[2]]);
        }

        insertIndex += 1;
      }
    }

    // overlapping - cut start of existing slice
    if (slice[0] <= newSlice[1] && slice[1] > newSlice[1]) {
      if (slice[2]?.blurLevel === newSlice[2]?.blurLevel) {
        newSlice[1] = slice[1]; // same blur level then will merge newSlice with right slice
      } else {
        if (fps) {
          const newSliceEndIndex = findFrameIndex(newSlice[1] - 1, fps); // subtract 1 ms to ensure get current frame
          const rightSliceStart = Math.ceil(
            frameAlignStartTimeMs(
              convertFrameIndexToTimeMs(newSliceEndIndex + 1, fps),
              fps
            )
          );
          outSlices.push([rightSliceStart, slice[1], slice[2]]);
        } else {
          outSlices.push([newSlice[1], slice[1], slice[2]]);
        }
      }
      return;
    }

    // after insert
    if (slice[0] >= newSlice[1]) {
      outSlices.push(slice);
    }
  });

  // now can insert new slice
  outSlices.splice(insertIndex, 0, newSlice);

  return outSlices;
};

/**
 * Takes a list of existing slices and remove a list of slices
 */
const subtractEditSlices = (
  initSlices: Array<BackgroundBlurSlice>,
  initCutSlices: Array<BackgroundBlurSlice>,
  fps?: number
): Array<BackgroundBlurSlice> => {
  /** remove cut slices from initSlices */
  const slices = sortBy(initSlices, '0');

  const cutSlices = initCutSlices;
  const newSlices: BackgroundBlurSlice[] = [];
  for (const slice of slices) {
    let [start] = slice;
    const [, end, details] = slice;
    for (const [cs, ce] of cutSlices) {
      // cut after current slice - break
      if (cs >= end) {
        break;
      }
      // cut before current slice - continue to next cut
      if (ce < start) {
        continue;
      }
      // must have overlap

      // inside current slice - create new slice to the left
      if (cs > start) {
        // create new slice to the left
        newSlices.push([start, cs, details]); // new slice up to cut
      }
      start = ce;
    }

    // add remainder of slice
    if (start < end) {
      newSlices.push([start, end, details]);
    }
  }

  // frame alignment
  if (fps) {
    return newSlices.map((s) => [
      Math.ceil(frameAlignStartTimeMs(s[0], fps)),
      Math.floor(frameAlignStopTimeMs(s[1], fps)),
      s[2],
    ]);
  }

  return newSlices;
};
